import get from 'lodash/get';
import sum from 'lodash/sum';
import { action, makeObservable, observable } from 'mobx';

import type { InvalidPromoErrorReason } from '../../components/Dialogs/PromoDialogs/InvalidPromoDialog/types';
import type { Promotion } from '../../dto/promo.types';
import type { Wallet } from '../../dto/wallet.types';
import { analytics } from '../../services/analytics';
import type { EventAction, WidgetEventOptionalArgs } from '../../services/analytics/types';
import { getRequest, postRequest } from '../../services/api';
import { devError } from '../../utils/log';
import type { FeatureFetchStatus, IFeatureManagerStore } from '../feature-manager/types';
import type { RootStore } from '../RootStore';
import { Loadable } from '../traits/Loadable';

import type { PromotionResponse } from './types';

export class PromotionStore extends Loadable implements IFeatureManagerStore<'PromoGift'> {
  dialogTypes = ['PromoGift'] as const;

  private promotion?: Promotion;
  private promoErrorCode?: InvalidPromoErrorReason;

  private isDialogStarted = false;
  private setDialogStarted(value: boolean) {
    this.isDialogStarted = value;
  }

  private promoCode: string = '';
  private setPromoCode(value: string) {
    this.promoCode = value;
  }

  async fetch(): Promise<FeatureFetchStatus> {
    const promoCode = new URLSearchParams(window.location.search).get('pc');
    this.setPromoCode(promoCode || '');

    if (!promoCode) {
      this.markPromotionFlowFinished();

      return 'OFF';
    }

    try {
      const res = await getRequest<PromotionResponse>('promotion/verify-promo-code', { promoCode });

      if (res.data?.success) {
        const { data: promotion } = res.data;
        this.promotion = promotion;
      } else if (res.data?.invalidReason) {
        this.promoErrorCode = res.data.invalidReason;
      } else {
        this.promoErrorCode = 'InternalServerErrorType';
      }
    } catch (error) {
      this.promoErrorCode = get(error, 'response.data.errors.[0].name', 'InternalServerErrorType');
    }

    return 'READY';
  }

  startDialogs() {
    if (this.isDialogStarted) {
      return false;
    }
    if (this.promotion) {
      if (typeof this.promoListener !== 'function') {
        return false;
      }

      this.promoListener(this.promotion);
      this.setDialogStarted(true);
      this.sendAnalyticsEvent({ event_action: 'impression', triggered_by: 'link' });
      return true;
    }

    if (this.promoErrorCode) {
      if (typeof this.promoErrorListener !== 'function') {
        return false;
      }

      this.promoErrorListener(this.promoErrorCode);
      this.setDialogStarted(true);
      this.sendAnalyticsEvent({ event_action: 'impression', triggered_by: 'link' });

      return true;
    }

    return false;
  }

  public sendAnalyticsEvent(args: { event_action: EventAction } & WidgetEventOptionalArgs) {
    try {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      void analytics.sendEvent('widget', {
        ...args,
        feature_order: this.rootStore.featureManagerStore.dialogManagerData?.PromoGift.order || 0,
        feature_type: 'promo_gift',
        feature_sub_type: 'promo_gift',
        promo_code: this.promoCode,
        ...this.promotion,
        ...(this.promoErrorCode && { promoErrorCode: this.promoErrorCode }),
      });
    } catch (e) {
      devError('promotionStore.sendAnalyticsEvent', e);
    }
  }

  private readonly rootStore: RootStore;

  constructor(rootStore: RootStore) {
    super();
    this.rootStore = rootStore;
    makeObservable(this);
  }

  private promoListener: ((promotion: Promotion) => void) | null = null;
  private promoErrorListener: ((errorCode: InvalidPromoErrorReason) => void) | null = null;

  addPromoListener(listener: (promotion: Promotion) => void) {
    this.promoListener = listener;
  }

  addPromoErrorListener(listener: (errorCode: InvalidPromoErrorReason) => void) {
    this.promoErrorListener = listener;
  }

  removePromoListener(listener: (promotion: Promotion) => void) {
    this.promoListener = listener;
  }

  removePromoErrorListener(listener: (errorCode: InvalidPromoErrorReason) => void) {
    this.promoErrorListener = listener;
  }

  get isPromoListenersEmpty() {
    return this.promoListener === null;
  }

  get isPromoErrorListenersEmpty() {
    return this.promoErrorListener === null;
  }

  @observable
  public isPromotionFlowFinished: boolean = false;

  @action
  public markPromotionFlowFinished() {
    this.isPromotionFlowFinished = true;
  }

  public closePromotionDialog() {
    if (!this.isPromotionFlowFinished) {
      this.markPromotionFlowFinished();
    }

    this.rootStore.portalStore.closePortal();
  }

  public async redeemPromoCode(promoCode: string): Promise<null | Pick<Wallet, 'gcCoin' | 'scCoin' | 'totalScCoin'>> {
    try {
      const res = await postRequest('promotion/redeem-promo-code', { promoCode });
      if (res.data) {
        const {
          data: { gcCoin, scCoin },
        } = res.data;
        const totalScCoin = sum(Object.values(scCoin));
        return { gcCoin, scCoin, totalScCoin };
      } else {
        return null;
      }
    } catch (err) {
      return null;
    }
  }
}
